import React, { useState, useEffect } from "react";
import { Button, Typography, Grid, Container, TextField } from "@mui/material";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import api from "../../globals/axios";
import {
  SERVER_BASE_ADDRESS,
  AGRI_COMMODITY_TYPE_CONVENTION,
  PROCESSING_PLANT_TYPE_CONVENTION,
  PRODUCT_TYPE_CONVENTION,
} from "../../globals/const";
import { getProcessingEmissions } from "../../globals/emissions";
import { getWalletFromLocal } from "../../globals/authentication";
import { getBlockchainNodeUrl } from "../../globals/const";
import CustomAppBar from "../../components/AppBar";

const styles = {
  icon: {
    marginRight: 2,
  },
  mainContent: {
    // backgroundColor: palette.background.paper,
    padding: 0.25,
    paddingLeft: 0.5,
    paddingRight: 0.5,
    flexGrow: 1,
  },
  row: {
    item: true,
    container: true,
    spacing: 1,
    justify: "center",
  },
  fab: {
    position: "fixed",
    bottom: 2,
    left: 2,
  },
  fabRow: {},
  fundSelectorRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  buttonRow: {
    margin: "10px",
    display: "flex",
    justifyContent: "space-around",
  },
};

export default function CreateBatch() {
  const classes = styles;

  const [token, setTokenCode] = useState("");
  const [wallet, setWallet] = useState({});
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [initiatorWallet, setIntiatorWallet] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [productCount, setProductCount] = useState(100);
  const [productCode, setProductCode] = useState("");
  const [factory, setFactory] = useState("");
  const [walletBalances, setWalletBalances] = useState([]);
  const [factories, setFactories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [electricity, setElectricity] = useState([]);
  const [productionQuality, setProductionQuality] = useState("");
  const [processingDate, setProcessingDate] = useState("");
  const [emission, setEmission] = useState([]);
  const [recipeList, setRecipeList] = useState([]);

  useEffect(() => {
    loadProductRecipe();
  }, []);

  useEffect(() => {
    /**
     * Get pending transfers from backend and set pendingTransfers to
     * [{
     *   'initiator_wallet' : (str) address for the initiator of the i-th pending smart contract
     *   'parent_token_code' : (str) token_code for the i-th token that has to be claimed
     *   'transfer_amount' : (int) amount of this token you are recieving
     * }]
     */
    const setData = async () => {
      const res = await api.get(
        `${SERVER_BASE_ADDRESS}/chain/pending-transfers`
      );
      setPendingTransfers(res.data.pending_transfers);
    };
    setData();
  }, []);

  async function loadProductRecipe() {
    const res = await api.get("/foodprocessor/product-recipe");
    if (res.status === 200) {
      console.log(res.data);
      setRecipeList(res.data);
    }
  }

  useEffect(() => {
    const loadWallet = async () => {
      const w = await getWalletFromLocal();
      setWallet(w);
    };

    loadWallet();
  }, []);

  useEffect(() => {
    api
      .get(
        getBlockchainNodeUrl() +
          "/get-balances?balance_type=ALL_TOKENS_IN_WALLET&wallet_address=" +
          wallet.address +
          "&token_type=" +
          AGRI_COMMODITY_TYPE_CONVENTION
      )
      .then((res) => res.data.balance)
      .then((data) => {
        if (data[0]) {
          data.forEach((token) => {
            api
              .get(
                getBlockchainNodeUrl() +
                  "/get-token?token_code=" +
                  token.token_code
              )
              .then((res) => res.data)
              .then((attributes) => {
                token.carbon_emission = JSON.parse(
                  attributes.token_attributes
                ).carbon_emission;
                try {
                  token.quality = JSON.parse(
                    attributes.token_attributes
                  ).harvest_quality;
                } catch {
                  token.quality = JSON.parse(
                    attributes.token_attributes
                  ).production_quality;
                }
                token.amountInWallet = token.balance;
              });
          });
          setWalletBalances(data);
        }
      });
  }, [wallet]);

  useEffect(() => {
    api
      .get(
        getBlockchainNodeUrl() +
          "/get-balances?balance_type=ALL_TOKENS_IN_WALLET&wallet_address=" +
          wallet.address +
          "&token_type=" +
          PROCESSING_PLANT_TYPE_CONVENTION
      )
      .then((res) => res.data.balance)
      .then((data) => {
        if (data[0]) {
          setFactories(data);
        }
      });
  }, [wallet]);

  // adds token_type field into wallets, amount of token in wallet, and emissions if relevant
  useEffect(() => {
    const addTypeAttribute = async () => {
      const data = walletBalances;
      await data.map((token) => {
        api
          .get(
            getBlockchainNodeUrl() + "/get-token?token_code=" + token.token_code
          )
          .then((res) => res.data)
          .then((data) => {
            if (
              data.tokentype === AGRI_COMMODITY_TYPE_CONVENTION ||
              data.tokentype === PRODUCT_TYPE_CONVENTION
            ) {
              token.carbon_emission = JSON.parse(
                data.token_attributes
              ).carbon_emission;
            }
            token.tokenType = data.tokentype;
            token.amountInWallet = token.balance;
            token.totalAmount = data.amount_created;
          });
      });
      setWalletBalances(data);
    };

    addTypeAttribute();
  }, [walletBalances]);

  useEffect(() => {
    /**Constant update of emissions */
    setEmission(getProcessingEmissions(electricity, 0));
  });

  const createNFT = async () => {
    const capitalSpaceRemovedProductName = productCode
      .replace(/\s/g, "")
      .toUpperCase();
    const childTokenCode = `BATCH-${capitalSpaceRemovedProductName}-${serialNumber.toUpperCase()}`;
    const product = recipeList.find((r) => r.serial_number === productCode);
    const tokenAttributes = {
      parent_token: token.parent_token_code,
      processing_plant: factory,
      product_name: product.product_name,
      serial_number: serialNumber,
      product_amount: productCount,
      ingredients: ingredients,
      electricity: electricity,
      production_date: processingDate,
      production_quality: productionQuality,
      carbon_emission: emission,
    };

    api
      .post(`${SERVER_BASE_ADDRESS}/chain/claim-token`, {
        initiator_wallet: initiatorWallet,
        parent_token_code: token.parent_token_code,
        child_token_code: childTokenCode,
        child_token_attributes: tokenAttributes,
      })
      .then((result) => {
        console.log(result);
        alert("Batch Created Successfully");
        window.open(".", "_self");
      })
      .catch((err) => {
        alert("Error - " + err.response.data.non_field_errors[0]);
      });
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <div className={classes.mainContent}>
          <Grid
            container
            // spacing={1}
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            <Typography variant="h5">Create Batch</Typography>

            <FormControl fullWidth>
              <InputLabel id="product-select-label">Product</InputLabel>
              <Select
                labelId="product-select-label"
                id="product-select"
                value={productCode}
                label="Select Product"
                onChange={async (e) => {
                  setProductCode(e.target.value);
                  const product = recipeList.find(
                    (r) => r.serial_number === e.target.value
                  );
                  console.log(product.ingredients_json);
                  setIngredients(product.ingredients_json);
                }}
              >
                {recipeList.map((b) => (
                  <MenuItem key={b.serial_number} value={b.serial_number}>
                    {b.product_name} - SN{b.serial_number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              label="Batch Number"
              id="txtamount"
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              label="Count/Quantity"
              type="number"
              id="txtamount"
              value={productCount}
              onChange={(event) => setProductCount(event.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="factoru-select-label">
                Processing Plant
              </InputLabel>
              <Select
                labelId="factoru-select-label"
                id="factory-select"
                value={factory}
                label="Select Processing Plant"
                onChange={(e) => {
                  setFactory(e.target.value);
                }}
              >
                {factories.map((b) => (
                  <MenuItem key={b.token_code} value={b.token_code}>
                    {b.token_code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {ingredients.map((ig, idx) => (
              <Grid key={idx} container justifyContent="space-between">
                <Grid xs={8}>
                  <FormControl fullWidth sx={{ marginTop: 2 }}>
                    <InputLabel id="token-select-label" helperText>
                      Select Token for {ig.type}
                    </InputLabel>
                    <Select
                      labelId="token-select-label"
                      id="token-select"
                      value={ingredients[idx].token_code}
                      label="Select Ingredient Token"
                      onChange={(e) => {
                        const newIngredients = [...ingredients];
                        newIngredients[idx].token_code =
                          e.target.value.parent_token_code;
                        newIngredients[idx].carbon_emission =
                          e.target.value.carbon_emission;
                        newIngredients[idx].amountInWallet =
                          e.target.value.amountInWallet;
                        newIngredients[idx].totalAmount =
                          e.target.value.totalAmount;
                        setIngredients(newIngredients);
                        setTokenCode(e.target.value);
                        setIntiatorWallet(e.target.value.initiator_wallet);
                      }}
                    >
                      {pendingTransfers.map((transfer) => (
                        <MenuItem
                          key={transfer.parent_token_code}
                          value={transfer}
                        >
                          {transfer.parent_token_code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={4}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="txtamount"
                    label="Proportion"
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    id="txtamount"
                    helperText={
                      ingredients[idx].amountInWallet
                        ? `Amount must be less than or equal to product owned: ${
                            ingredients[idx].amountInWallet / 10 ** 3
                          } kg`
                        : ""
                    }
                    value={token.transfer_amount}
                    onChange={(e) => {
                      const newIngredients = [...ingredients];
                      if (e.target.value < newIngredients[idx].amount) {
                        newIngredients[idx].proportion = e.target.value;
                      } else {
                        newIngredients[idx].proportion =
                          newIngredients[idx].amountInWallet;
                      }
                      setIngredients(newIngredients);
                    }}
                  />
                </Grid>
              </Grid>
            ))}

            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              label="Electricty Used (kW/hr)"
              type="number"
              id="txtamount"
              value={electricity}
              onChange={(event) => {
                setElectricity(event.target.value);
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              // label="Processing Date"
              type="date"
              id="txtamount"
              value={processingDate}
              onChange={(event) => setProcessingDate(event.target.value)}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              label="Production Quality"
              type="text"
              id="txtamount"
              value={productionQuality}
              onChange={(event) => setProductionQuality(event.target.value)}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="txtamount"
              label="CO2 Emission (kgs)"
              type="number"
              id="txtamount"
              value={emission}
            />

            <Button
              style={{ marginTop: 10 }}
              variant="contained"
              size="small"
              color="primary"
              onClick={createNFT}
            >
              Create Batch
            </Button>
          </Grid>
        </div>
      </Container>
    </>
  );
}
